"use client";

import { type ReactNode, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  HINT,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import {
  DatamuseCredit,
  ExampleWords,
  LookupState,
  revealField,
  WordChips,
} from "@/components/tools/vocabulary/word-parts";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  answeredParam,
  normalizeLookup,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";
import {
  filterRhymes,
  groupBySyllables,
  type RhymeOptions,
  rhymeQueries,
  SYLLABLE_FILTERS,
  type SyllableFilter,
} from "@/lib/tool-kit/vocabulary/rhymes";
import { useDatamuse } from "@/lib/tool-kit/vocabulary/use-datamuse";

/** English strings. A translated content file overrides these through `ui`. */
export const RHYME_FINDER_STRINGS = {
  wordLabel: "Word to rhyme",
  wordPlaceholder: "time",
  wordHint:
    "Results appear as you type. Click any result to find rhymes for that word.",
  tryLabel: "Try:",
  examples: ["time", "light", "heart", "orange", "memory"],
  emptyPrompt:
    "Type a word to find its perfect rhymes, near rhymes, and words that sound alike.",
  syllablesLabel: "Syllables",
  syllableOptions: {
    any: "Any",
    "1": "1",
    "2": "2",
    "3": "3",
    "4": "4+",
  } as Record<SyllableFilter, string>,
  phrasesLabel: "Include phrases such as “partner in crime”",
  rareLabel: "Include rare words",
  namesLabel: "Include names and places",
  heading: "Rhymes for “{word}”",
  summary: "{perfect} · {near} · {sound}",
  perfectCount: "one:{count} perfect rhyme|other:{count} perfect rhymes",
  nearCount: "one:{count} near rhyme|other:{count} near rhymes",
  soundCount: "one:{count} sound-alike|other:{count} sound-alikes",
  perfectHeading: "Perfect rhymes",
  perfectHint: "The same stressed vowel and every sound after it.",
  nearHeading: "Near rhymes",
  nearHint: "Close but not exact — slant or half rhymes.",
  soundHeading: "Sound-alikes",
  soundHint:
    "Words that sound like yours overall, rhyme or not — useful for puns and mnemonics.",
  syllableGroup: "one:{count} syllable|other:{count} syllables",
  unknownSyllables: "Syllable count not known",
  none: "None found.",
  hiddenCount:
    "one:{count} more word hidden by the filters above.|other:{count} more words hidden by the filters above.",
  copyList: "Copy list",
  copied: "Copied!",
  searching: "Searching…",
  errorNetwork:
    "Could not reach the Datamuse word service. Check your connection and try again.",
  errorBusy:
    "Datamuse is receiving too many requests right now. Wait a minute and try again.",
  errorUnavailable:
    "The Datamuse word service did not answer. Try again in a moment.",
  retry: "Try again",
  credit: "Word data from the Datamuse API",
} as const;

type Strings = typeof RHYME_FINDER_STRINGS;

const FIELD_ID = "rhyme-word";
const MAX_INPUT = 40;
const RE_LETTER = /\p{L}/u;
const UNFILTERED: RhymeOptions = {
  syllables: "any",
  phrases: true,
  rare: true,
  names: true,
};

function RhymeSection({
  heading,
  hint,
  words,
  hidden,
  strings,
  children,
}: {
  heading: string;
  hint: string;
  words: readonly string[];
  hidden: number;
  strings: Strings;
  children: ReactNode;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={WIDGET_H3}>{heading}</h3>
        {words.length > 0 ? (
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyList}
            small
            text={words.join(", ")}
          />
        ) : null}
      </div>
      <p className={HINT}>{hint}</p>
      {words.length > 0 ? children : <Notice>{strings.none}</Notice>}
      {hidden > 0 ? (
        <p className={HINT}>
          {tPlural(strings.hiddenCount, locale, hidden, {
            count: fmt(hidden),
          })}
        </p>
      ) : null}
    </section>
  );
}

function Results({
  word,
  answers,
  options,
  onSelect,
  stale,
  strings,
}: {
  word: string;
  answers: readonly (readonly WordResult[])[];
  options: RhymeOptions;
  onSelect: (word: string) => void;
  stale: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [perfect, near, sound] = answers.map((answer) =>
    filterRhymes(word, answer, options)
  );
  const [allPerfect, allNear, allSound] = answers.map(
    (answer) => filterRhymes(word, answer, UNFILTERED).length
  );
  const groupLabel = (syllables: number | null) =>
    syllables === null
      ? strings.unknownSyllables
      : tPlural(strings.syllableGroup, locale, syllables, {
          count: fmt(syllables),
        });
  const words = (list: readonly WordResult[]) =>
    list.map((result) => result.word);

  return (
    <div className={stale ? "space-y-6 opacity-60" : "space-y-6"}>
      <div aria-live="polite" className="space-y-1">
        <h2 className={WIDGET_H2}>{t(strings.heading, { word })}</h2>
        <p className={HINT}>
          {t(strings.summary, {
            perfect: tPlural(strings.perfectCount, locale, perfect.length, {
              count: fmt(perfect.length),
            }),
            near: tPlural(strings.nearCount, locale, near.length, {
              count: fmt(near.length),
            }),
            sound: tPlural(strings.soundCount, locale, sound.length, {
              count: fmt(sound.length),
            }),
          })}
        </p>
      </div>
      <RhymeSection
        heading={strings.perfectHeading}
        hidden={allPerfect - perfect.length}
        hint={strings.perfectHint}
        strings={strings}
        words={words(perfect)}
      >
        {groupBySyllables(perfect).map((group) => (
          <div className="space-y-2" key={group.syllables ?? "unknown"}>
            <p className={LABEL}>{groupLabel(group.syllables)}</p>
            <WordChips onSelect={onSelect} words={group.words} />
          </div>
        ))}
      </RhymeSection>
      <RhymeSection
        heading={strings.nearHeading}
        hidden={allNear - near.length}
        hint={strings.nearHint}
        strings={strings}
        words={words(near)}
      >
        <WordChips onSelect={onSelect} words={words(near)} />
      </RhymeSection>
      <RhymeSection
        heading={strings.soundHeading}
        hidden={allSound - sound.length}
        hint={strings.soundHint}
        strings={strings}
        words={words(sound)}
      >
        <WordChips onSelect={onSelect} words={words(sound)} />
      </RhymeSection>
    </div>
  );
}

export default function RhymeFinderWidget() {
  const strings = useToolUi(RHYME_FINDER_STRINGS);
  const [input, setInput] = useState("");
  const [syllables, setSyllables] = useState<SyllableFilter>("any");
  const [phrases, setPhrases] = useState(false);
  const [rare, setRare] = useState(false);
  const [names, setNames] = useState(false);
  const normalized = normalizeLookup(input);
  const word = RE_LETTER.test(normalized) ? normalized : "";
  const lookup = useDatamuse(word ? rhymeQueries(word) : []);
  const answeredWord = answeredParam(lookup.answered, "rel_rhy");
  const ready = word && lookup.results.length === 3;

  function lookUp(next: string) {
    setInput(next);
    revealField(FIELD_ID);
  }

  return (
    <>
      <TextField
        hint={strings.wordHint}
        id={FIELD_ID}
        label={strings.wordLabel}
        onChange={(value) => setInput(value.slice(0, MAX_INPUT))}
        placeholder={strings.wordPlaceholder}
        type="search"
        value={input}
      />
      <div className="space-y-4">
        <PillGroup
          label={strings.syllablesLabel}
          name="rhyme-syllables"
          onChange={setSyllables}
          options={SYLLABLE_FILTERS.map((value) => ({
            value,
            label: strings.syllableOptions[value],
          }))}
          value={syllables}
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={phrases}
            label={strings.phrasesLabel}
            onChange={setPhrases}
          />
          <Checkbox
            checked={rare}
            label={strings.rareLabel}
            onChange={setRare}
          />
          <Checkbox
            checked={names}
            label={strings.namesLabel}
            onChange={setNames}
          />
        </div>
      </div>
      {word ? (
        <LookupState
          failure={lookup.failure}
          onRetry={lookup.retry}
          status={lookup.status}
          strings={strings}
        />
      ) : (
        <div className="space-y-3">
          <Notice>{strings.emptyPrompt}</Notice>
          <ExampleWords
            label={strings.tryLabel}
            onSelect={lookUp}
            words={strings.examples}
          />
        </div>
      )}
      {ready ? (
        <Results
          answers={lookup.results}
          onSelect={lookUp}
          options={{ syllables, phrases, rare, names }}
          stale={lookup.status === "loading"}
          strings={strings}
          word={answeredWord}
        />
      ) : null}
      <DatamuseCredit label={strings.credit} />
    </>
  );
}
